import { Controller } from "@hotwired/stimulus"

// Zero-based budgeting "assign" screen. The amount inputs inside the vials are
// the real form fields; this controller only reads/writes them and paints the
// derived UI (liquid heights, hero counter, CTA state).
export default class extends Controller {
  static targets = ["total", "hero", "subline", "vial", "liquid", "amount",
    "stepDown", "stepUp", "cta", "live", "grid", "vialTemplate", "addSheet", "preset",
    "eyebrow", "pct", "keptInput", "keptLiquid", "keptPct", "keptTile", "goalNote"]
  static outlets = ["sortable"]
  static values = {
    step: { type: Number, default: 10 },
    symbol: String,
    prefix: Boolean,
    locale: String,
    confirmLabel: String,
    savePartialLabel: String,
    overLabel: String,
    assignedOfLabel: String,
    doneLabel: String,
    leftAnnounce: String,
    leftLabel: String,
    budgetedLabel: String,
    colors: Array
  }

  // Vial fill scale: a vial reads visually full at 40% of the total.
  static FILL_FRACTION = 0.4
  // Liquid never rises past this height so the icon + name header always sits
  // on the card background, never on saturated liquid.
  static MAX_FILL_PERCENT = 62

  connect() {
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    // A Turbo cache restore can bring back a page that was left mid-edit.
    // Every vial starts locked, whatever the restored markup says.
    this.closeEditing()
    this.paint()
  }

  disconnect() {
    this.stopStepRepeat()
    clearTimeout(this.liveTimer)
    window.floosyoDirty = false
  }

  // ── State (inputs are the source of truth) ──────────────────────────────

  amounts() {
    return this.amountTargets.map((input) => this.parse(input.value))
  }

  total() {
    return this.parse(this.totalTarget.value)
  }

  // Savings held aside by the "Split by %" card counts as assigned: it comes
  // off the income before the vials, it just never becomes a budget.
  sum() {
    return this.amounts().reduce((acc, v) => acc + v, 0) + this.reserved()
  }

  splitOn() {
    return Boolean(this.split?.on)
  }

  // Every kept card's share in split mode: Savings and Debt (never budgets),
  // plus a Needs / Wants budget Confirm is about to create.
  reserved() {
    return this.keptInputTargets.reduce((acc, input) => acc + this.keptPart(input.dataset.key), 0)
  }

  // Kept-aside amount (savings / debt): what the user set on its card, else
  // the split's share of the income.
  keptPart(key) {
    if (!this.splitOn()) return 0
    return this.keptOverride?.[key] ?? this.share(key)
  }

  // Income is optional. Without it there is nothing to divide up: vials take
  // any amount, the hero reads their sum, and Confirm is always live.
  hasIncome() {
    return this.total() > 0
  }

  // What fills are drawn against: the income, or the sum of the
  // vials when none was typed.
  scale() {
    return this.hasIncome() ? this.total() : this.sum()
  }

  // The most a vial may hold: what is left of the income plus its own value,
  // or anything at all when no income was typed.
  cap(index) {
    return this.hasIncome() ? this.available(index) : Number.MAX_SAFE_INTEGER
  }

  available(index) {
    return Math.max(0, this.total() - (this.sum() - this.amounts()[index]))
  }

  // One slot's share of the income, floored like UserSetting#split_share so
  // the shares never add up to more than the income and match what is saved.
  share(key) {
    return Math.floor(this.total() * this.split[key] / 100)
  }

  parse(value) {
    return parseInt(String(value).replace(/[^\d]/g, ""), 10) || 0
  }

  setAmount(index, value) {
    const clamped = Math.max(0, Math.min(value, this.cap(index)))
    this.amountTargets[index].value = clamped
    this.markDirty()
    this.paint()
  }

  // Native close (X) reads this flag to decide whether to confirm discarding.
  markDirty() {
    window.floosyoDirty = true
  }

  // ── Painting ─────────────────────────────────────────────────────────────

  paint() {
    if (this.splitOn() && !this.applyingSplit) this.#splitFromVials()

    const amounts = this.amounts()
    const total = this.total()
    const hasIncome = total > 0
    const sum = amounts.reduce((acc, v) => acc + v, 0) + this.reserved()
    const scale = hasIncome ? total : sum
    const fillScale = Math.max(1, scale * this.constructor.FILL_FRACTION)
    const left = total - sum
    const done = hasIncome && left === 0

    amounts.forEach((value, i) => {
      this.liquidTargets[i].style.height = `${Math.min(this.constructor.MAX_FILL_PERCENT, (value / fillScale) * 100)}%`
      this.vialTargets[i].setAttribute("aria-valuenow", value)
      if (hasIncome) {
        this.vialTargets[i].setAttribute("aria-valuemax", value + Math.max(0, left))
      } else {
        this.vialTargets[i].removeAttribute("aria-valuemax")
      }
      // Never rewrite the input the user is typing in; blurAmount normalizes it.
      if (document.activeElement !== this.amountTargets[i]) {
        this.amountTargets[i].value = value
      }
      this.stepDownTargets[i].disabled = value <= 0
      this.stepUpTargets[i].disabled = hasIncome && left <= 0
    })

    // With an income the hero is what is left of it; without one it is simply
    // what the vials add up to, and the helpers that split an income go away.
    this.eyebrowTarget.textContent = hasIncome ? this.leftLabelValue : this.budgetedLabelValue
    // Fully assigned: the "every dollar has a job" line replaces the $0 in
    // the hero slot and the card compacts (empty subline drops out).
    if (done) {
      this.heroTarget.dataset.shown = "done"
      this.heroTarget.textContent = this.doneLabelValue
    } else {
      this.rollHero(hasIncome ? Math.max(0, left) : sum)
    }
    this.heroTarget.classList.toggle("done", done)
    this.sublineTarget.textContent = this.sublineText(hasIncome, done, sum, total)
    this.sublineTarget.classList.toggle("hidden", done)

    this.#paintSplit(total)
    this.paintCta(left, done, hasIncome)
    this.announce(left, done, hasIncome, sum)
  }

  // ── Split by % (budget-split card) ──────────────────────────────────────

  // budget-split:change. Fills the Needs / Wants vials from the percents and
  // holds Savings aside. The initial event (page load) only records the
  // split: the vials already show the saved limits.
  applySplit(event) {
    this.split = event.detail
    this.keptOverride = null
    // A preset or Custom tap starts over: a card removed with its x may
    // come back. Only a kept card's own add / remove keeps the record.
    if (!event.detail.keepVials) this.removedKept = null
    // keepVials: a kept card added from the sheet, whose split was read off
    // the vials, so they keep exactly what the user typed.
    if (this.splitOn() && !event.detail.initial && !event.detail.keepVials) this.#fillFromSplit()
    if (!event.detail.initial) this.markDirty()
    this.applyingSplit = true
    this.paint()
    this.applyingSplit = false
  }

  // Income typed: in split mode the Needs / Wants vials follow it.
  totalInput() {
    this.keptOverride = null
    if (this.splitOn()) {
      this.#fillFromSplit()
      this.applyingSplit = true
      this.paint()
      this.applyingSplit = false
    } else {
      this.paint()
    }
  }

  #fillFromSplit() {
    this.amountTargets.forEach((input) => {
      const kind = input.dataset.kind
      if (kind) input.value = this.share(kind)
    })
  }

  // A Needs / Wants vial was edited by hand: the split follows the vials, Debt
  // keeps its share while it fits, and Savings takes whatever is left.
  #splitFromVials() {
    const total = this.total()
    if (total <= 0) return

    // kind "" is a Free budget's vial.
    const vials = (kind) => this.amountTargets.filter((input) => (input.dataset.kind || "") === kind)
    const share = (kind) => vials(kind).reduce((acc, input) => acc + this.parse(input.value), 0)
    // Clear all empties the vials; that's not a new split (it would read as
    // 100% savings), so the split stays where it was.
    if (this.amounts().every((amount) => amount === 0)) return

    // A kind with no vial yet (created on Confirm) keeps its percent.
    const pct = (kind) => (vials(kind).length ? Math.round(share(kind) / total * 100) : this.split[kind])
    const need = Math.min(100, pct("need"))
    const want = Math.min(100 - need, pct("want"))
    // Free budgets hold their share of the income too, so Savings only takes
    // what is really left.
    const free = Math.min(100 - need - want, Math.round(share("") / total * 100))
    const debt = Math.min(this.split.debt, 100 - need - want - free)
    // A Savings card removed with its x stays removed: what is left goes
    // back to Left to assign instead.
    const savings = this.removedKept?.savings ? 0 : 100 - need - want - free - debt
    if (need === this.split.need && want === this.split.want && savings === this.split.savings &&
        debt === this.split.debt) return

    this.split = { ...this.split, need, want, savings, debt }
    this.keptOverride = null
    this.dispatch("vialsEdited", { detail: this.split })
  }

  // Savings / Debt card typed or stepped: that exact amount is kept aside
  // each month. Capped at what the other slots leave, so the split stays
  // <= 100%.
  keptInput(event) {
    this.#setKept(event.target.dataset.key, this.parse(event.target.value))
  }

  keptBlur() {
    this.applyingSplit = true
    this.paint()
    this.applyingSplit = false
  }

  // Same press-and-hold steppers as a vial (stepStart).
  keptStepStart(event) {
    event.preventDefault()
    const button = event.currentTarget
    const key = button.dataset.key
    const dir = Number(button.dataset.dir)
    const tick = () => {
      const current = this.keptPart(key)
      this.#setKept(key, Math.max(0, Math.round((current + this.stepValue * dir) / this.stepValue) * this.stepValue))
    }
    tick()
    this.stepTimer = setTimeout(() => this.repeatStep(button, tick), 340)
    window.addEventListener("pointerup", this.stopStepRepeat, { once: true })
  }

  // Same drag-the-liquid gesture as a vial (dragStart): only while the card
  // is unlocked with its pencil, never on the number itself.
  keptDragStart(event) {
    if (!this.editing(event.currentTarget)) return
    if (event.target.closest(".assign-val")) return
    event.preventDefault()
    const card = event.currentTarget
    const key = card.dataset.key
    const total = this.total()
    if (!this.splitOn() || total <= 0) return

    card.setPointerCapture(event.pointerId)
    const rect = card.getBoundingClientRect()
    const other = key === "savings" ? "debt" : "savings"
    const room = Math.floor(total * (100 - this.split.need - this.split.want - this.split[other]) / 100)
    const fillScale = Math.max(1, this.scale() * this.constructor.FILL_FRACTION)
    const dragMax = Math.max(fillScale, room)
    const move = (ev) => {
      const fraction = Math.max(0, Math.min(1, 1 - (ev.clientY - rect.top) / rect.height))
      this.#setKept(key, Math.round((fraction * dragMax) / this.stepValue) * this.stepValue)
    }
    const stop = () => {
      card.removeEventListener("pointermove", move)
      card.removeEventListener("pointerup", stop)
      card.removeEventListener("pointercancel", stop)
    }
    card.addEventListener("pointermove", move)
    card.addEventListener("pointerup", stop)
    card.addEventListener("pointercancel", stop)
    move(event)
  }

  #setKept(key, value) {
    const total = this.total()
    if (!this.splitOn() || total <= 0) return

    const other = key === "savings" ? "debt" : "savings"
    const room = 100 - this.split.need - this.split.want - this.split[other]
    const amount = Math.min(value, Math.floor(total * room / 100))
    this.keptOverride = { ...this.keptOverride, [key]: amount }
    this.split = { ...this.split, [key]: Math.round(amount / total * 100) }
    this.dispatch("vialsEdited", { detail: this.split })
    this.markDirty()
    this.applyingSplit = true
    this.paint()
    this.applyingSplit = false
  }

  // Kept card values and the goal note on the Savings card.
  #paintSavings() {
    this.keptInputTargets.forEach((input) => {
      if (document.activeElement !== input) input.value = this.keptPart(input.dataset.key)
    })
    // Savings / Debt badges: the split's percent (the cards only show in
    // split mode).
    this.keptPctTargets.forEach((badge) => {
      badge.textContent = this.splitOn() ? `${this.split[badge.dataset.key]}%` : ""
    })
    // Liquid fill on the same scale as the vials (paint).
    const fillScale = Math.max(1, this.scale() * this.constructor.FILL_FRACTION)
    this.keptLiquidTargets.forEach((liquid) => {
      const value = this.keptPart(liquid.dataset.key)
      liquid.style.height = `${Math.min(this.constructor.MAX_FILL_PERCENT, (value / fillScale) * 100)}%`
    })
    if (!this.hasGoalNoteTarget) return

    // The server saves the savings share as a whole percent of the income
    // (UserSetting#apply_budget_split!), so the note promises that, not a
    // hand-typed amount the percent rounds away from.
    const monthly = this.splitOn() ? this.share("savings") : 0

    // The card already says Savings: the note is only the year's amount.
    const note = this.goalNoteTarget
    note.textContent = note.dataset.label.replace("__target__", this.money(monthly * 12))
  }

  #paintSplit(total) {
    this.pctTargets.forEach((badge) => {
      const input = this.amountTargets[Number(badge.dataset.index)]
      const show = this.splitOn() && total > 0 && input
      badge.classList.toggle("hidden", !show)
      if (show) badge.textContent = `${Math.round(this.parse(input.value) / total * 100)}%`
    })
    this.#paintSavings()
  }

  sublineText(hasIncome, done, sum, total) {
    if (!hasIncome) return ""
    if (done) return ""
    return this.assignedOfLabelValue
      .replace("__assigned__", this.money(sum))
      .replace("__total__", this.money(total))
  }

  // Never disabled. Over-assigning is shown, not blocked: the server saves
  // whatever the vials hold and the dashboard band reads the shortfall in red.
  paintCta(left, done, hasIncome) {
    const cta = this.ctaTarget
    cta.classList.remove("ready", "over")
    if (!hasIncome) {
      cta.textContent = this.confirmLabelValue
    } else if (done) {
      cta.classList.add("ready")
      cta.textContent = this.confirmLabelValue
    } else if (left > 0) {
      cta.textContent = this.savePartialLabelValue.replace("__amount__", this.money(left))
    } else {
      cta.classList.add("over")
      cta.textContent = this.overLabelValue.replace("__amount__", this.money(-left))
    }
  }

  rollHero(target) {
    const hero = this.heroTarget
    if (this.reduceMotion) {
      hero.dataset.shown = target
      hero.textContent = this.money(target)
      return
    }
    const from = parseInt(hero.dataset.shown, 10) || 0
    hero.dataset.shown = target
    const start = performance.now()
    const tick = (now) => {
      if (hero.dataset.shown !== String(target)) return
      const k = Math.min(1, (now - start) / 280)
      const eased = 1 - Math.pow(1 - k, 3)
      hero.textContent = this.money(Math.round(from + (target - from) * eased))
      if (k < 1 && parseInt(hero.dataset.shown, 10) === target) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  money(value) {
    const number = Math.round(value).toLocaleString(this.localeValue)
    return this.prefixValue ? `${this.symbolValue}${number}` : `${number}${this.symbolValue}`
  }

  announce(left, done, hasIncome, sum) {
    clearTimeout(this.liveTimer)
    this.liveTimer = setTimeout(() => {
      this.liveTarget.textContent = this.announcement(left, done, hasIncome, sum)
    }, 400)
  }

  announcement(left, done, hasIncome, sum) {
    if (!hasIncome) return `${this.budgetedLabelValue} ${this.money(sum)}`
    if (done) return this.doneLabelValue
    return this.leftAnnounceValue.replace("__amount__", this.money(Math.abs(left)))
  }

  // ── Typing ───────────────────────────────────────────────────────────────

  selectAmount(event) {
    event.target.select()
  }

  typeAmount() {
    // Repaint from the raw typed value; over-assignment surfaces in the CTA
    // ("over" state) and gets clamped on blur.
    this.markDirty()
    this.paint()
  }

  // Income field: reformat with grouping separators once typing ends. Latin
  // numbering system always — parse() only understands ASCII digits, and the
  // value round-trips through the form to the server.
  formatTotal() {
    const total = this.total()
    this.totalTarget.value = total > 0 ? total.toLocaleString(`${this.localeValue}-u-nu-latn`) : ""
    this.paint()
  }

  blurAmount(event) {
    const index = Number(event.target.dataset.index)
    event.target.value = Math.min(this.parse(event.target.value), this.cap(index))
    this.paint()
  }

  // ── Edit mode ────────────────────────────────────────────────────────────
  // A locked vial is a drag handle for reordering and nothing else: the slider
  // is dead, the number is readonly, and the steppers are collapsed to zero
  // width. So a long press on a vial cannot move money, no matter where on the
  // card it lands or how far the finger travels. Tapping Edit unlocks exactly
  // one vial, and unlocking suspends dragging — the two gestures share the same
  // pixels, so only one of them is ever live.

  toggleEdit(event) {
    const cell = event.currentTarget.closest("[data-vial-cell]")
    const wasEditing = cell.classList.contains("editing")
    this.closeEditing()
    if (!wasEditing) this.openEditing(cell)
  }

  openEditing(cell) {
    cell.classList.add("editing")
    // Kept-aside cells (savings / debt) have a value but no vial slider.
    cell.querySelector("[data-budget-assignment-target=amount], [data-kept-input]").readOnly = false
    cell.querySelector("[data-budget-assignment-target=vial]")?.removeAttribute("aria-readonly")
    cell.querySelector(".assign-edit")?.setAttribute("aria-pressed", "true")
    this.sortableOutlets.forEach((sortable) => sortable.setDisabled(true))
  }

  closeEditing() {
    this.element.querySelectorAll("[data-vial-cell].editing").forEach((cell) => {
      cell.classList.remove("editing")
      const amount = cell.querySelector("[data-budget-assignment-target=amount], [data-kept-input]")
      // Blur first: an input that is focused when it turns readonly keeps the
      // caret and the keyboard open with nothing to type into.
      if (document.activeElement === amount) amount.blur()
      amount.readOnly = true
      cell.querySelector("[data-budget-assignment-target=vial]")?.setAttribute("aria-readonly", "true")
      cell.querySelector(".assign-edit")?.setAttribute("aria-pressed", "false")
    })
    this.sortableOutlets.forEach((sortable) => sortable.setDisabled(false))
  }

  editing(element) {
    return element.closest("[data-vial-cell]")?.classList.contains("editing") === true
  }

  // ── Dragging ─────────────────────────────────────────────────────────────

  dragStart(event) {
    // Bail before preventDefault so a locked vial leaves the gesture to
    // Sortable (and to the page's own scrolling) completely untouched.
    if (!this.editing(event.currentTarget)) return
    if (event.target.closest(".assign-val")) return
    event.preventDefault()
    const vial = event.currentTarget
    const index = Number(vial.dataset.index)
    vial.setPointerCapture(event.pointerId)
    vial.classList.add("grabbing")
    const rect = vial.getBoundingClientRect()
    // Drag spans the full amount this vial can take (its current value plus
    // whatever is left), never less than the visual fill scale — otherwise a
    // vial reads full while money remains and only +/- can assign the rest.
    // Without an income there is no "left": the drag reaches the fill scale
    // of the current sum, and typing or the steppers go beyond it.
    const fillScale = Math.max(1, this.scale() * this.constructor.FILL_FRACTION)
    const dragMax = Math.max(fillScale, this.hasIncome() ? this.available(index) : this.amounts()[index])
    const move = (ev) => {
      const fraction = Math.max(0, Math.min(1, 1 - (ev.clientY - rect.top) / rect.height))
      this.setAmount(index, Math.round((fraction * dragMax) / this.stepValue) * this.stepValue)
    }
    const stop = () => {
      vial.classList.remove("grabbing")
      vial.removeEventListener("pointermove", move)
      vial.removeEventListener("pointerup", stop)
      vial.removeEventListener("pointercancel", stop)
    }
    vial.addEventListener("pointermove", move)
    vial.addEventListener("pointerup", stop)
    vial.addEventListener("pointercancel", stop)
    move(event)
  }

  // ── Keyboard slider ──────────────────────────────────────────────────────

  keyAdjust(event) {
    if (!this.editing(event.currentTarget)) return
    const index = Number(event.currentTarget.dataset.index)
    const value = this.amounts()[index]
    const step = this.stepValue
    const map = {
      ArrowUp: value + step,
      ArrowRight: value + step,
      ArrowDown: value - step,
      ArrowLeft: value - step,
      PageUp: value + step * 4,
      PageDown: value - step * 4,
      Home: 0,
      End: this.hasIncome() ? this.available(index) : value
    }
    if (!(event.key in map)) return
    event.preventDefault()
    this.setAmount(index, map[event.key])
  }

  // ── Steppers (with press-and-hold repeat) ────────────────────────────────

  stepStart(event) {
    event.preventDefault()
    const button = event.currentTarget
    const index = Number(button.dataset.index)
    const dir = Number(button.dataset.dir)
    const tick = () => {
      const current = this.amounts()[index]
      this.setAmount(index, Math.round((current + this.stepValue * dir) / this.stepValue) * this.stepValue)
    }
    tick()
    this.stepTimer = setTimeout(() => this.repeatStep(button, tick), 340)
    window.addEventListener("pointerup", this.stopStepRepeat, { once: true })
  }

  repeatStep(button, tick) {
    if (button.disabled) return
    tick()
    this.stepTimer = setTimeout(() => this.repeatStep(button, tick), 95)
  }

  stopStepRepeat = () => {
    clearTimeout(this.stepTimer)
  }

  // ── Quick actions ────────────────────────────────────────────────────────

  reset() {
    this.amountTargets.forEach((input) => { input.value = input.dataset.initial })
    this.markDirty()
    this.paint()
  }

  clearAll() {
    this.amountTargets.forEach((input) => { input.value = 0 })
    this.markDirty()
    this.paint()
  }

  // ── Adding budgets client-side ──────────────────────────────────────────
  // New vials live only in the DOM; the server creates the budgets on Confirm
  // (funded ones only), so in-progress assignments are never lost to a reload.

  // Savings / Debt need an income to take a share of, and room left in it.
  openAddSheet() {
    const total = this.total()
    this.keptTileTargets.forEach((tile) => {
      const key = tile.dataset.key
      const shown = this.splitOn() && this.split[key] > 0
      tile.disabled = total <= 0 || shown || this.#keptFromVials(key)[key] <= 0
    })
    this.addSheetTarget.classList.remove("hidden")
    this.addSheetTarget.classList.add("flex")
  }

  closeAddSheet() {
    this.addSheetTarget.classList.add("hidden")
    this.addSheetTarget.classList.remove("flex")
  }

  // Savings / Debt tapped in the add sheet: the split turns on with that card
  // holding what the vials leave (budget-split#adopt), saved on Confirm.
  addKept(event) {
    const key = event.currentTarget.dataset.key
    if (this.total() <= 0) return

    this.closeAddSheet()
    this.removedKept = { ...this.removedKept, [key]: false }
    this.dispatch("keptAdded", { detail: this.#keptFromVials(key) })
  }

  // x on a Savings / Debt card (Custom only): that card drops to 0% and its
  // money goes back to Left to assign. The other card keeps what it holds;
  // with no kept card left the split turns off (budget-split#drop).
  removeKept(event) {
    const key = event.currentTarget.dataset.key
    const other = key === "savings" ? "debt" : "savings"
    const otherAmount = this.keptOverride?.[other]
    const split = { ...this.split, [key]: 0 }

    this.closeEditing()
    this.removedKept = { ...this.removedKept, [key]: true }
    this.dispatch("keptRemoved", { detail: { ...split, on: split[other] > 0 } })

    // applySplit dropped the overrides; the card that stays keeps its amount.
    if (otherAmount != null && this.splitOn()) {
      this.keptOverride = { [other]: otherAmount }
      this.applyingSplit = true
      this.paint()
      this.applyingSplit = false
    }
  }

  // The split a kept card added from the sheet makes: Needs / Wants at the
  // shares their vials hold, the new card at what is left, as whole percents
  // of the income. When the other kept card already holds all of it (Savings
  // takes the remainder), the new card takes half of that card's share, so
  // Debt can always join Savings and the other way round.
  #keptFromVials(key) {
    const total = this.total()
    const pct = (kind) => Math.round(this.amountTargets
      .filter((input) => (input.dataset.kind || "") === kind)
      .reduce((acc, input) => acc + this.parse(input.value), 0) / total * 100)
    const need = Math.min(100, pct("need"))
    const want = Math.min(100 - need, pct("want"))
    const free = Math.min(100 - need - want, pct(""))
    const other = key === "savings" ? "debt" : "savings"
    const room = 100 - need - want - free
    let kept = this.splitOn() ? Math.min(this.split[other], room) : 0
    let share = room - kept
    if (share <= 0 && kept > 0) {
      share = Math.floor(kept / 2)
      kept -= share
    }
    return { need, want, [other]: kept, [key]: share }
  }

  addPreset(event) {
    const button = event.currentTarget
    const name = button.dataset.name
    const icon = button.dataset.icon
    const index = this.vialTargets.length
    const color = this.colorsValue[index % this.colorsValue.length]
    const uid = `n${Date.now()}`

    const html = this.vialTemplateTarget.innerHTML
      .replaceAll("__INDEX__", String(index))
      .replaceAll("__UID__", uid)
      .replaceAll("__COLOR__", color)
    this.gridTarget.insertAdjacentHTML("beforeend", html)

    const cell = this.gridTarget.lastElementChild
    cell.dataset.presetName = name
    cell.querySelector("[data-role=name]").textContent = name
    cell.querySelector("[data-role=nameField]").value = name
    cell.querySelector("[data-role=iconField]").value = icon
    cell.querySelector("[data-budget-assignment-target=vial]").setAttribute("aria-label", name)
    cell.querySelector("[data-role=icon]").src = button.querySelector("img").src

    button.disabled = true
    this.closeAddSheet()
    this.markDirty()
    this.paint()
  }

  // A vial was dragged to a new slot. Its hidden budget_assignment[order][]
  // token travelled inside the cell, so the order that submits on Confirm is
  // already correct. What has to catch up is every cached index.
  vialsReordered() {
    this.reindex()
    this.markDirty()
    this.paint()
  }

  removeVial(event) {
    // Closing first: a cell removed while still marked editing would take the
    // suspended-dragging flag with it and never give it back.
    this.closeEditing()
    const cell = event.currentTarget.closest("[data-role=newVial]")
    const preset = this.presetTargets.find((b) => b.dataset.name === cell.dataset.presetName)
    if (preset) preset.disabled = false
    cell.remove()
    this.reindex()
    this.markDirty()
    this.paint()
  }

  // New vials always sit at the tail of the grid, so removing one
  // only shifts the indexes of later new vials — but every index-carrying node
  // gets rewritten to keep target arrays and dataset indexes aligned.
  reindex() {
    this.vialTargets.forEach((vial, i) => {
      vial.dataset.index = String(i)
      const cell = vial.parentElement
      cell.querySelectorAll("[data-index]").forEach((node) => { node.dataset.index = String(i) })
    })
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  // Nothing is guarded: an over-assigned plan saves and the band shows the
  // shortfall in red. Only the dirty flag is cleared, so the native close does
  // not ask about discarding a plan that was just saved.
  submitted() {
    window.floosyoDirty = false
  }
}
